export type CVLanguage = 'es' | 'en' | 'fr' | 'pt' | 'de'

export interface CVLabels {
  summary: string
  experience: string
  education: string
  skills: string
  languages: string
  technical: string
  soft: string
  tools: string
  present: string
  gpa: string
  profile: string
  competencies: string
  interpersonal: string
  contact: string
}

const translations: Record<CVLanguage, CVLabels> = {
  es: {
    summary: 'Resumen',
    experience: 'Experiencia',
    education: 'Educación',
    skills: 'Habilidades',
    languages: 'Idiomas',
    technical: 'Técnicas',
    soft: 'Blandas',
    tools: 'Herramientas',
    present: 'Presente',
    gpa: 'Promedio',
    profile: 'Perfil Profesional',
    competencies: 'Competencias',
    interpersonal: 'Interpersonales',
    contact: 'Contacto',
  },
  en: {
    summary: 'Summary',
    experience: 'Experience',
    education: 'Education',
    skills: 'Skills',
    languages: 'Languages',
    technical: 'Technical',
    soft: 'Soft Skills',
    tools: 'Tools',
    present: 'Present',
    gpa: 'GPA',
    profile: 'Professional Profile',
    competencies: 'Competencies',
    interpersonal: 'Interpersonal',
    contact: 'Contact',
  },
  fr: {
    summary: 'Résumé',
    experience: 'Expérience',
    education: 'Formation',
    skills: 'Compétences',
    languages: 'Langues',
    technical: 'Techniques',
    soft: 'Savoir-être',
    tools: 'Outils',
    present: 'Présent',
    gpa: 'Moyenne',
    profile: 'Profil Professionnel',
    competencies: 'Compétences',
    interpersonal: 'Relationnel',
    contact: 'Contact',
  },
  pt: {
    summary: 'Resumo',
    experience: 'Experiência',
    education: 'Educação',
    skills: 'Habilidades',
    languages: 'Idiomas',
    technical: 'Técnicas',
    soft: 'Interpessoais',
    tools: 'Ferramentas',
    present: 'Presente',
    gpa: 'Média',
    profile: 'Perfil Profissional',
    competencies: 'Competências',
    interpersonal: 'Interpessoais',
    contact: 'Contato',
  },
  de: {
    summary: 'Zusammenfassung',
    experience: 'Berufserfahrung',
    education: 'Ausbildung',
    skills: 'Fähigkeiten',
    languages: 'Sprachen',
    technical: 'Technische',
    soft: 'Soft Skills',
    tools: 'Werkzeuge',
    present: 'Heute',
    gpa: 'Notendurchschnitt',
    profile: 'Berufliches Profil',
    competencies: 'Kompetenzen',
    interpersonal: 'Soziale',
    contact: 'Kontakt',
  },
}

export function getCVLabels(language?: CVLanguage, customLabels?: Partial<CVLabels>): CVLabels {
  const base = translations[language || 'en'] || translations.en
  if (!customLabels) return base
  return { ...base, ...Object.fromEntries(Object.entries(customLabels).filter(([, v]) => v)) }
}

export const CV_LANGUAGE_OPTIONS: { value: CVLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'pt', label: 'Português' },
]

// Helper to format "Present" in the correct language
export function getPresentLabel(language?: CVLanguage): string {
  return getCVLabels(language).present
}

const levelTranslations: Record<CVLanguage, Record<string, string>> = {
  es: { native: 'Nativo', fluent: 'Fluido', advanced: 'Avanzado', intermediate: 'Intermedio', basic: 'Básico' },
  en: { native: 'Native', fluent: 'Fluent', advanced: 'Advanced', intermediate: 'Intermediate', basic: 'Basic' },
  fr: { native: 'Natif', fluent: 'Courant', advanced: 'Avancé', intermediate: 'Intermédiaire', basic: 'Débutant' },
  pt: { native: 'Nativo', fluent: 'Fluente', advanced: 'Avançado', intermediate: 'Intermediário', basic: 'Básico' },
  de: { native: 'Muttersprache', fluent: 'Fließend', advanced: 'Fortgeschritten', intermediate: 'Mittelstufe', basic: 'Grundkenntnisse' },
}

export function getLevelLabel(level: string | undefined, language?: CVLanguage): string {
  if (!level) return ''
  const lang = language || 'en'
  return levelTranslations[lang]?.[level] || level
}
